import type { Block, EditorDocument, LocalizedText, ThemeTokens } from "@dishfolio/contracts";
import { EDITOR_SCHEMA_VERSION } from "@dishfolio/contracts";

export * from "./fonts.ts";

export type ThemeBuildContext = {
  restaurantName: string;
  defaultLanguage: string;
  /** Optional hero image to use in the layout. */
  heroImageAssetId?: string | null;
  hasLogo?: boolean;
  orderingEnabled?: boolean;
};

export type ThemePreset = {
  key: string;
  name: string;
  description: string;
  version: number;
  isPremium: boolean;
  tokens: ThemeTokens;
  /** Default block layout used for new menus or when the user resets layout. */
  buildBlocks: (ctx: ThemeBuildContext) => Block[];
  preview: { swatches: string[] };
};

let counter = 0;
/** Short, CSS-safe, random block id. */
export function blockId(prefix = "b"): string {
  counter = (counter + 1) % 1296;
  const rand = Math.random().toString(36).slice(2, 8);
  return `${prefix}_${rand}${counter.toString(36)}`;
}

const t = (lang: string, text: string): LocalizedText => ({ [lang]: text });

function block(type: string, props: Record<string, unknown> = {}, extra: Partial<Block> = {}): Block {
  return { id: blockId(), type, props, ...extra };
}

const basic: ThemePreset = {
  key: "basic",
  name: "Basic",
  description: "Clean, readable and fast. The free-plan theme.",
  version: 1,
  isPremium: false,
  tokens: {
    primaryColor: "#2f6f4f",
    backgroundColor: "#ffffff",
    surfaceColor: "#f6f7f5",
    textColor: "#1c1f1d",
    mutedColor: "#5f6762",
    accentColor: "#d97b29",
    fontHeading: "Inter",
    fontBody: "Inter",
    radius: 10,
    spacing: 1,
    headingCase: "none",
    priceStyle: "right",
    imageStyle: "rounded",
  },
  preview: { swatches: ["#ffffff", "#2f6f4f", "#d97b29", "#1c1f1d"] },
  buildBlocks: (ctx) => [
    block("section", {}, {
      style: { paddingTop: 32, paddingBottom: 16, paddingLeft: 16, paddingRight: 16, textAlign: "center" },
      children: [
        ...(ctx.hasLogo ? [block("logo", { size: 72 })] : []),
        block("heading", { text: t(ctx.defaultLanguage, ctx.restaurantName), level: 1 }),
        block("restaurant-details", { showAddress: true, showPhone: true, showWebsite: false, layout: "inline" }, { style: { color: "muted", fontSize: "sm" } }),
        block("language-selector", { style: "pills" }, { style: { marginTop: 12 } }),
      ],
    }),
    block("category-nav", { style: "pills", sticky: true }),
    block("section", {}, {
      style: { paddingTop: 8, paddingBottom: 32, paddingLeft: 16, paddingRight: 16 },
      children: [block("menu-category-list", { categoryIds: [], layout: "list", columns: 1, showImages: true, showDescriptions: true })],
    }),
    block("allergen-badges", { mode: "legend" }, { style: { paddingLeft: 16, paddingRight: 16, paddingBottom: 24 } }),
    block("cart-button", { label: t(ctx.defaultLanguage, "View order"), position: "floating" }),
  ],
};

const elegantDark: ThemePreset = {
  key: "elegant-dark",
  name: "Elegant Dark",
  description: "Premium dining: deep palette, gold accents and serif headings.",
  version: 2,
  isPremium: true,
  tokens: {
    primaryColor: "#c9a66b",
    backgroundColor: "#24211e",
    surfaceColor: "#2e2a26",
    textColor: "#f3ede4",
    mutedColor: "#b5aa9b",
    accentColor: "#c9a66b",
    fontHeading: "Playfair Display",
    fontBody: "Inter",
    radius: 2,
    spacing: 1.15,
    headingCase: "none",
    priceStyle: "dotted",
    imageStyle: "square",
  },
  preview: { swatches: ["#24211e", "#c9a66b", "#f3ede4", "#2e2a26"] },
  buildBlocks: (ctx) => [
    block("hero", {
      title: t(ctx.defaultLanguage, ctx.restaurantName),
      subtitle: t(ctx.defaultLanguage, "Our Menu"),
      imageAssetId: ctx.heroImageAssetId ?? null,
      overlay: 40,
      height: "md",
      align: "center",
    }),
    block("language-selector", { style: "pills" }, { style: { textAlign: "center", paddingTop: 20, paddingBottom: 12 } }),
    block("category-nav", { style: "underline", sticky: true }),
    block("container", {}, {
      style: { maxWidth: 820, paddingTop: 16, paddingBottom: 48 },
      children: [block("menu-category-list", { categoryIds: [], layout: "list", columns: 1, showImages: false, showDescriptions: true })],
    }),
    block("divider", { style: "ornament", thickness: 1 }, { style: { color: "primary" } }),
    block("restaurant-details", { showAddress: true, showPhone: true, showWebsite: true, layout: "stacked" }, { style: { textAlign: "center", paddingBottom: 48, color: "muted" } }),
    block("allergen-badges", { mode: "legend" }, { style: { textAlign: "center", paddingBottom: 32 } }),
    block("cart-button", { label: t(ctx.defaultLanguage, "Your order"), position: "floating" }),
  ],
};

const minimalCafe: ThemePreset = {
  key: "minimal-cafe",
  name: "Minimal Café",
  description: "Bright, editorial café presentation with plenty of white space.",
  version: 2,
  isPremium: true,
  tokens: {
    primaryColor: "#1f1f1f",
    backgroundColor: "#fbfaf7",
    surfaceColor: "#ffffff",
    textColor: "#1f1f1f",
    mutedColor: "#7a7570",
    accentColor: "#e0a458",
    fontHeading: "DM Serif Display",
    fontBody: "DM Sans",
    radius: 16,
    spacing: 1.1,
    headingCase: "none",
    priceStyle: "inline",
    imageStyle: "rounded",
  },
  preview: { swatches: ["#fbfaf7", "#1f1f1f", "#e0a458", "#ffffff"] },
  buildBlocks: (ctx) => [
    block("section", {}, {
      style: { paddingTop: 40, paddingBottom: 8, paddingLeft: 20, paddingRight: 20 },
      children: [
        block("heading", { text: t(ctx.defaultLanguage, ctx.restaurantName), level: 1 }, { style: { fontSize: "4xl" } }),
        block("text", { text: t(ctx.defaultLanguage, "Coffee, pastries and seasonal plates.") }, { style: { color: "muted", marginTop: 8 } }),
        block("search", { placeholder: t(ctx.defaultLanguage, "Search the menu") }, { style: { marginTop: 20 } }),
        block("language-selector", { style: "pills" }, { style: { marginTop: 12 } }),
      ],
    }),
    block("category-nav", { style: "pills", sticky: true }),
    block("section", {}, {
      style: { paddingTop: 8, paddingLeft: 20, paddingRight: 20, paddingBottom: 40 },
      children: [
        block("menu-category-list", { categoryIds: [], layout: "list", columns: 1, showImages: true, showDescriptions: true }, {
          overrides: { tablet: { props: { columns: 2 } } },
        }),
      ],
    }),
    block("restaurant-details", { showAddress: true, showPhone: true, showWebsite: false, layout: "inline" }, { style: { paddingLeft: 20, paddingRight: 20, paddingBottom: 24, color: "muted", fontSize: "sm" } }),
    block("allergen-badges", { mode: "legend" }, { style: { paddingLeft: 20, paddingRight: 20, paddingBottom: 32 } }),
    block("cart-button", { label: t(ctx.defaultLanguage, "Order"), position: "floating" }),
  ],
};

const boldBistro: ThemePreset = {
  key: "bold-bistro",
  name: "Bold Bistro",
  description: "Strong colour, big imagery and bold item cards.",
  version: 2,
  isPremium: true,
  tokens: {
    primaryColor: "#e63946",
    backgroundColor: "#fff8f0",
    surfaceColor: "#ffffff",
    textColor: "#1d1d1d",
    mutedColor: "#5c5552",
    accentColor: "#ffb703",
    fontHeading: "Archivo Black",
    fontBody: "Rubik",
    radius: 14,
    spacing: 1,
    headingCase: "uppercase",
    priceStyle: "right",
    imageStyle: "rounded",
  },
  preview: { swatches: ["#fff8f0", "#e63946", "#ffb703", "#1d1d1d"] },
  buildBlocks: (ctx) => [
    block("hero", {
      title: t(ctx.defaultLanguage, ctx.restaurantName),
      subtitle: t(ctx.defaultLanguage, "Fresh. Loud. Delicious."),
      imageAssetId: ctx.heroImageAssetId ?? null,
      overlay: 25,
      height: "md",
      align: "left",
    }),
    block("category-nav", { style: "pills", sticky: true }),
    block("section", {}, {
      style: { paddingTop: 24, paddingBottom: 40, paddingLeft: 16, paddingRight: 16 },
      children: [
        block("menu-category-list", { categoryIds: [], layout: "list", columns: 1, showImages: true, showDescriptions: true }, {
          overrides: { tablet: { props: { columns: 2 } } },
        }),
      ],
    }),
    block("language-selector", { style: "pills" }, { style: { textAlign: "center", paddingBottom: 16 } }),
    block("restaurant-details", { showAddress: true, showPhone: true, showWebsite: true, layout: "stacked" }, { style: { textAlign: "center", paddingBottom: 32 } }),
    block("allergen-badges", { mode: "legend" }, { style: { textAlign: "center", paddingBottom: 32 } }),
    block("cart-button", { label: t(ctx.defaultLanguage, "My order"), position: "floating" }),
  ],
};

const rusticClassic: ThemePreset = {
  key: "rustic-classic",
  name: "Rustic Classic",
  description: "Warm palette and traditional typography for classic restaurants.",
  version: 2,
  isPremium: true,
  tokens: {
    primaryColor: "#8c3b1f",
    backgroundColor: "#f5ecd9",
    surfaceColor: "#efe3c8",
    textColor: "#3a2a1c",
    mutedColor: "#76604a",
    accentColor: "#5a7247",
    fontHeading: "Libre Baskerville",
    fontBody: "Lora",
    radius: 4,
    spacing: 1.1,
    headingCase: "uppercase",
    priceStyle: "dotted",
    imageStyle: "square",
  },
  preview: { swatches: ["#f5ecd9", "#8c3b1f", "#5a7247", "#3a2a1c"] },
  buildBlocks: (ctx) => [
    block("section", {}, {
      style: { paddingTop: 40, paddingBottom: 8, textAlign: "center" },
      children: [
        ...(ctx.hasLogo ? [block("logo", { size: 96 })] : []),
        block("heading", { text: t(ctx.defaultLanguage, ctx.restaurantName), level: 1 }, { style: { color: "primary" } }),
        block("divider", { style: "ornament", thickness: 1 }, { style: { color: "primary" } }),
        block("text", { text: t(ctx.defaultLanguage, "Traditional cooking since always.") }, { style: { color: "muted", fontSize: "sm" } }),
        block("language-selector", { style: "pills" }, { style: { marginTop: 12 } }),
      ],
    }),
    block("category-nav", { style: "underline", sticky: true }),
    block("container", {}, {
      style: { maxWidth: 760, paddingTop: 16, paddingBottom: 40 },
      children: [block("menu-category-list", { categoryIds: [], layout: "list", columns: 1, showImages: false, showDescriptions: true })],
    }),
    block("restaurant-details", { showAddress: true, showPhone: true, showWebsite: true, layout: "stacked" }, { style: { textAlign: "center", paddingBottom: 24 } }),
    block("allergen-badges", { mode: "legend" }, { style: { textAlign: "center", paddingBottom: 32 } }),
    block("cart-button", { label: t(ctx.defaultLanguage, "Your order"), position: "floating" }),
  ],
};

const modernEditorial: ThemePreset = {
  key: "modern-editorial",
  name: "Modern Editorial",
  description: "Large imagery and asymmetrical, magazine-like sections.",
  version: 2,
  isPremium: true,
  tokens: {
    primaryColor: "#111111",
    backgroundColor: "#f2f0eb",
    surfaceColor: "#ffffff",
    textColor: "#111111",
    mutedColor: "#6b6b6b",
    accentColor: "#ff5a36",
    fontHeading: "Fraunces",
    fontBody: "Space Grotesk",
    radius: 0,
    spacing: 1.25,
    headingCase: "none",
    priceStyle: "inline",
    imageStyle: "square",
  },
  preview: { swatches: ["#f2f0eb", "#111111", "#ff5a36", "#ffffff"] },
  buildBlocks: (ctx) => [
    block("hero", {
      title: t(ctx.defaultLanguage, ctx.restaurantName),
      subtitle: t(ctx.defaultLanguage, "Seasonal menu"),
      imageAssetId: ctx.heroImageAssetId ?? null,
      overlay: 15,
      height: "md",
      align: "left",
    }),
    block("section", {}, {
      style: { paddingTop: 28, paddingBottom: 8, paddingLeft: 24, paddingRight: 24 },
      children: [
        block("text", { text: t(ctx.defaultLanguage, "Everything is made in house, every day, with produce from people we know.") }, { style: { fontSize: "lg", maxWidth: 560 } }),
        block("language-selector", { style: "pills" }, { style: { marginTop: 16 } }),
      ],
    }),
    block("category-nav", { style: "underline", sticky: true }),
    block("section", {}, {
      style: { paddingTop: 24, paddingBottom: 48, paddingLeft: 24, paddingRight: 24 },
      children: [
        block("menu-category-list", { categoryIds: [], layout: "list", columns: 1, showImages: true, showDescriptions: true }, {
          overrides: { tablet: { props: { columns: 2 } } },
        }),
      ],
    }),
    block("restaurant-details", { showAddress: true, showPhone: true, showWebsite: true, layout: "inline" }, { style: { paddingLeft: 24, paddingRight: 24, paddingBottom: 24, color: "muted" } }),
    block("allergen-badges", { mode: "legend" }, { style: { paddingLeft: 24, paddingRight: 24, paddingBottom: 32 } }),
    block("cart-button", { label: t(ctx.defaultLanguage, "Order"), position: "floating" }),
  ],
};

export const THEMES: ThemePreset[] = [basic, elegantDark, minimalCafe, boldBistro, rusticClassic, modernEditorial];
export const DEFAULT_THEME_KEY = "basic";

export function getTheme(key: string): ThemePreset | undefined {
  return THEMES.find((th) => th.key === key);
}

export function buildThemeDocument(themeKey: string, ctx: ThemeBuildContext, tokenOverrides?: Partial<ThemeTokens>): EditorDocument {
  const theme = getTheme(themeKey) ?? basic;
  return {
    schemaVersion: EDITOR_SCHEMA_VERSION,
    themeId: theme.key,
    themeVersion: theme.version,
    tokens: { ...theme.tokens, ...tokenOverrides },
    pages: [{ id: "main", blocks: theme.buildBlocks(ctx) }],
  };
}

/** A minimal blank layout: header + category list only. */
export function buildBlankDocument(ctx: ThemeBuildContext): EditorDocument {
  return {
    schemaVersion: EDITOR_SCHEMA_VERSION,
    themeId: basic.key,
    themeVersion: basic.version,
    tokens: { ...basic.tokens },
    pages: [
      {
        id: "main",
        blocks: [
          block("heading", { text: t(ctx.defaultLanguage, ctx.restaurantName), level: 1 }, { style: { paddingTop: 24, paddingLeft: 16, paddingRight: 16 } }),
          block("menu-category-list", { categoryIds: [], layout: "list", columns: 1, showImages: true, showDescriptions: true }, { style: { paddingLeft: 16, paddingRight: 16, paddingBottom: 24 } }),
        ],
      },
    ],
  };
}

/**
 * Theme switching changes presentation only: tokens (and optionally layout) are replaced while
 * canonical menu data is untouched. Localized text the user wrote is carried over when the
 * layout is kept.
 */
export function switchTheme(doc: EditorDocument, themeKey: string, opts: { resetLayout: boolean; ctx: ThemeBuildContext }): EditorDocument {
  const theme = getTheme(themeKey);
  if (!theme) throw new Error(`Unknown theme ${themeKey}`);
  if (opts.resetLayout) return buildThemeDocument(themeKey, opts.ctx);
  return { ...doc, themeId: theme.key, themeVersion: theme.version, tokens: { ...theme.tokens } };
}
